import { useEffect, useState, useCallback } from "react";
import { CustomDropdown } from "../../../../component/customdropdown/CustomDropdown";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  selectInventorySpareFilterState,
  setInventorySparesFilter,
} from "../../../../store/inventory/inventorySparesSlice";

import { toast } from "react-toastify";
import axiosInstance from "../../../../axios/axiosTokenMiddleware";
import { ArrowDownTrayIcon, ArrowUpTrayIcon } from "@heroicons/react/24/solid";
import classes from "./inventorySparesFilterPage.module.css";
import { Table } from "../../../../component/table/Table";
import { FileUploadLoader } from "../../../../component/spinners/fileUploadLoader/FileUploadLoader";

/* ---------- Upload API ---------- */
const uploadUrl =
  "https://dev.backend.mobigarage.com/v1/mp/sellers/inventory/spares/upload_file    ";

const uploadImageRequest = async (file) => {
  const formData = new FormData();
  formData.append("uploaded_file", file);

  try {
    const response = await axiosInstance.post(uploadUrl, formData);

    return {
      success: true,
      code: response.data.code,
      data: response.data.data || [],
      message: response.data.message?.displayMessage,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error?.response?.data?.message?.displayMessage ||
        "Upload failed",
    };
  }
};

/* ---------- Constants ---------- */
const statuses = [
  { id: "", label: "Select Status" },
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

const sellerList = [];

export const InventorySparesFilterPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useSelector(selectInventorySpareFilterState);

  const [failedItems, setFailedItems] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  /* ---------- Sync URL & Redux ---------- */
  useEffect(() => {
    const seller_id = searchParams.get("seller_id") || "";
    const status = searchParams.get("status") || "";

    if (
      seller_id !== filters.seller_id ||
      status !== filters.status
    ) {
      dispatch(setInventorySparesFilter({ seller_id, status }));
    }
  }, [
    searchParams,
    filters.seller_id,
    filters.status,
    dispatch,
  ]);

  /* ---------- Update URL ---------- */
  const updateFilterParams = useCallback(
    (key, value) => {
      const params = new URLSearchParams(searchParams);
      value ? params.set(key, value) : params.delete(key);
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleSelection = (key, value) => {
    updateFilterParams(key, value);
  };

  /* ---------- Upload Handler ---------- */
  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    setFailedItems([]);
    setIsUploading(true);

    try {
      const result = await uploadImageRequest(selectedFile);

      if (!result.success) {
        toast.error(result.error);
        return;
      }

      if (result.code !== "414") {
        toast.success("File uploaded successfully!");
      } else {
        toast.warning(
          result.message || "Some rows failed to process"
        );
        setFailedItems(result.data);
      }
    } catch (err) {
      toast.error("Unexpected error occurred.");
    } finally {
      setIsUploading(false);
      event.target.value = null;
    }
  };

  /* ---------- Download Template ---------- */
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href =
      "https://aws-mg-new-bucket.s3.ap-south-1.amazonaws.com/mg_template_spares_file.xlsx";
    link.download = "mg_template_inventory_file.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ---------- Render ---------- */
  return (
    <div className={classes.box}>
      {/* Filters */}
      <div className={classes.box__content}>
        <CustomDropdown
          id="seller_id"
          label="Select Seller"
          options={sellerList}
          selectedId={Number(filters.seller_id) || ""}
          onSelection={handleSelection}
        />
      </div>

      <div className={classes.box__content}>
        <CustomDropdown
          id="status"
          label="Select Status"
          options={statuses}
          selectedId={filters.status || ""}
          onSelection={handleSelection}
        />
      </div>

      {/* Actions */}
      <div className={classes.actions}>
        <input
          id="inventory-upload-input"
          type="file"
          hidden
          accept=".xlsx,.xls,.csv"
          onChange={handleFileChange}
          disabled={isUploading}
        />

        <label
          htmlFor="inventory-upload-input"
          className={classes.actionBtn}
          style={{
            pointerEvents: isUploading ? "none" : "auto",
            opacity: isUploading ? 0.6 : 1,
          }}
        >
          <ArrowUpTrayIcon className={classes.icon} />
          <span>
            {isUploading ? "Uploading..." : "Upload File"}
          </span>
        </label>

        <button
          className={classes.box__btn}
          onClick={handleDownload}
        >
          <ArrowDownTrayIcon className={classes.icon} />
          <span>Download Template</span>
        </button>
      </div>

      {/* Upload Loader */}
      {isUploading && (
        <FileUploadLoader message="Uploading inventory file, please wait..." />
      )}

      {/* Failed Items Modal */}
      {failedItems.length > 0 && (
        <div className={classes.modal} onClick={() => setFailedItems([])}>
          <div className={classes.modal__content}>
            <div className={classes.modal__content__head}>
              <h1 className={classes.modal__content__head__title}>
                List of mismatched items:
              </h1>
              <button
                className={classes.modal__close}
                onClick={() => setFailedItems([])}
              >
                Close
              </button>
            </div>

            <Table
              data={failedItems}
              header
              columns={[
                { accessorKey: "row_no", header: "Row" },
                { accessorKey: "brand", header: "Brand" },
                { accessorKey: "model", header: "Model" },
                { accessorKey: "part_name", header: "Part Name" },
                { accessorKey: "color", header: "Color" },
              ]}
            />
          </div>
        </div>
      )}
    </div>
  );
};